import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { JSMProjectDesignAssistant } from './JSMProjectDesignAssistant';
import { Building2, ArrowLeft, CheckCircle } from 'lucide-react';
import type { JSMSpaceAssessmentResponse } from '../types';

interface JSMAssessmentDemoProps {
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  isConsultant?: boolean;
  onComplete?: () => void;
  onBack?: () => void;
}

export function JSMAssessmentDemo({
  companyId,
  companyName,
  accessToken,
  currentUser,
  isConsultant = false,
  onComplete,
  onBack
}: JSMAssessmentDemoProps) {
  const [showAssistant, setShowAssistant] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [assessment, setAssessment] = useState<JSMSpaceAssessmentResponse | null>(null);

  const handleAssessmentComplete = (completedAssessment: JSMSpaceAssessmentResponse) => {
    setAssessment(completedAssessment);
    setCompleted(true);
    console.log('Assessment completed:', completedAssessment);
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
  };

  if (completed && assessment) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <CardTitle>Assessment Complete</CardTitle>
              <CardDescription>
                Your JSM Space design recommendation has been saved
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="mb-2">
              <strong>Recommended:</strong> {assessment.recommendedSpaceCount} JSM Space{assessment.recommendedSpaceCount > 1 ? 's' : ''}
            </h4>
            {assessment.recommendedStructure.map((space, index) => (
              <div key={index} className="mt-2 text-sm">
                <strong>{space.spaceName}:</strong> {space.teams.join(', ')}
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            Status: {assessment.status === 'approved' ? 'Approved by consultant' : 
                     assessment.status === 'submitted' ? 'Awaiting consultant review' :
                     'Draft'}
          </p>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowAssistant(true)}>
              View Assessment
            </Button>
            {assessment.status === 'approved' && (
              <Button onClick={handleContinue}>
                Continue to Configuration
              </Button>
            )}
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showAssistant) {
    return (
      <div className="space-y-4">
        {onBack && (
          <Button variant="ghost" onClick={() => setShowAssistant(false)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
        <JSMProjectDesignAssistant
          companyId={companyId}
          companyName={companyName}
          accessToken={accessToken}
          currentUser={currentUser}
          isConsultant={isConsultant}
          onComplete={handleAssessmentComplete}
          onSave={(savedAssessment) => {
            console.log('Assessment saved:', savedAssessment);
          }}
        />
      </div>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div>
            <CardTitle>JSM Space Design Assistant</CardTitle>
            <CardDescription>
              Determine the optimal JSM Space structure for your organization
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Before we begin configuring your Jira Service Management implementation, it's important to 
            determine <strong>how many JSM Spaces (Projects)</strong> you'll need.
          </p>
          
          <p className="text-sm text-muted-foreground">
            This is a critical architectural decision that affects:
          </p>
          
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2">
            <li>Customer experience and portal structure</li>
            <li>Team autonomy and workflows</li>
            <li>Privacy and data access controls</li>
            <li>Reporting and metrics</li>
            <li>Administration complexity</li>
          </ul>

          <p className="text-sm text-muted-foreground">
            The <strong>JSM Space Design Assistant</strong> will guide you through 8 key questions to 
            help determine the best structure for your organization.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 not-prose">
            <h4 className="mb-2">What to expect:</h4>
            <ul className="space-y-1 text-sm">
              <li>8 sections covering organization structure, customer experience, and more</li>
              <li>Educational content and guiding insights</li>
              <li>Personalized recommendation based on your responses</li>
              <li>Consultant review and approval</li>
              <li>Approximately 10-15 minutes to complete</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button onClick={() => setShowAssistant(true)} size="lg">
            Start Assessment
          </Button>
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              Skip for Now
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}